import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
  Table,
  Badge,
} from "reactstrap";
import { fetchApi } from "redux/action/api_tbeams";
import { useToasts } from "helper/ToastAdapter";
import moment from "moment";
import { formatNumber } from "redux/action/api";

const ApprovalHistoryModal = ({ isOpen, toggle, approvalNo, releaseData }) => {
  const { addToast } = useToasts();
  const [isLoading, setIsLoading] = useState(false);
  const [historyData, setHistoryData] = useState([]);

  useEffect(() => {
    if (isOpen && approvalNo) {
      fetchHistory();
    }
  }, [isOpen, approvalNo]);

  const fetchHistory = async () => {
    setIsLoading(true);
    try {
      const response = await fetchApi(
        `get_approval_history_details?approval_no=${encodeURIComponent(approvalNo)}`,
      );

      if (response.success) {
        setHistoryData(response.result || []);
      } else {
        addToast("Failed to fetch approval history", {
          appearance: "error",
          autoDismiss: true,
        });
      }
    } catch (error) {
      console.error("Error fetching approval history:", error);
      addToast("Error loading approval history", {
        appearance: "error",
        autoDismiss: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    if (status?.includes("Released")) return "success";
    if (status?.includes("Returned")) return "danger";
    if (status?.includes("Reviewed")) return "info";
    if (status?.includes("Initiated")) return "primary";
    if (status?.includes("Controls")) return "warning";
    return "secondary";
  };

  const getTimeColor = (seconds) => {
    if (!seconds) return "#6c757d";
    const hours = seconds / 3600;
    if (hours < 24) return "#28a745"; // Green - less than 1 day
    if (hours < 72) return "#ffc107"; // Yellow - 1-3 days
    return "#dc3545"; // Red - more than 3 days
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" scrollable>
      <ModalHeader toggle={toggle} style={{ backgroundColor: "#f8f9fa" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <i
            className="fas fa-history"
            style={{ fontSize: "24px", color: "#51ce5bff" }}
          ></i>
          <div>
            <h5 style={{ margin: 0, color: "#333" }}>Approval History</h5>
            <small style={{ color: "#666" }}>
              Reference: <strong>{approvalNo}</strong>
            </small>
          </div>
        </div>
      </ModalHeader>

      <ModalBody style={{ backgroundColor: "#f8f9fa", padding: "20px" }}>
        {/* Workflow Stage Guide */}
        <div
          style={{
            backgroundColor: "#fff",
            padding: "20px",
            borderRadius: "8px",
            marginBottom: "20px",
            border: "1px solid #e0e0e0",
          }}
        >
          <h6
            style={{
              marginBottom: "15px",
              color: "#333",
              borderBottom: "2px solid #51ce5bff",
              paddingBottom: "10px",
            }}
          >
            <i
              className="fas fa-route"
              style={{ marginRight: "8px", color: "#51ce5bff" }}
            ></i>
            Approval Workflow Stages
          </h6>

          {/* Budget Office Stages */}
          <div style={{ marginBottom: "15px" }}>
            <div
              style={{
                display: "inline-block",
                padding: "4px 10px",
                backgroundColor: "#e3f2fd",
                borderRadius: "4px",
                fontSize: "12px",
                fontWeight: "600",
                color: "#1976d2",
                marginBottom: "10px",
              }}
            >
              <i className="fas fa-building" style={{ marginRight: "6px" }}></i>
              Budget Office Stages
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "8px",
              }}
            >
              <div
                style={{
                  padding: "8px 14px",
                  backgroundColor: "#f0f8ff",
                  border: "2px solid #4a90e2",
                  borderRadius: "6px",
                  fontSize: "11px",
                  fontWeight: "500",
                  color: "#1565c0",
                }}
              >
                1. Approval Initiated
              </div>
              <i
                className="fas fa-arrow-right"
                style={{ color: "#999", fontSize: "12px" }}
              ></i>
              <div
                style={{
                  padding: "8px 14px",
                  backgroundColor: "#f0f8ff",
                  border: "2px solid #4a90e2",
                  borderRadius: "6px",
                  fontSize: "11px",
                  fontWeight: "500",
                  color: "#1565c0",
                }}
              >
                2. Approvals Reviewed
              </div>
              <i
                className="fas fa-arrow-right"
                style={{ color: "#999", fontSize: "12px" }}
              ></i>
              <div
                style={{
                  padding: "8px 14px",
                  backgroundColor: "#f0f8ff",
                  border: "2px solid #4a90e2",
                  borderRadius: "6px",
                  fontSize: "11px",
                  fontWeight: "500",
                  color: "#1565c0",
                }}
              >
                3. Final Internal Controls
              </div>
              <i
                className="fas fa-arrow-right"
                style={{ color: "#999", fontSize: "12px" }}
              ></i>
              <div
                style={{
                  padding: "8px 14px",
                  backgroundColor: "#f0f8ff",
                  border: "2px solid #4a90e2",
                  borderRadius: "6px",
                  fontSize: "11px",
                  fontWeight: "500",
                  color: "#1565c0",
                }}
              >
                4. Approval Released
              </div>
              <div
                style={{
                  padding: "8px 14px",
                  backgroundColor: "#fff8e1",
                  border: "2px solid #ff9800",
                  borderRadius: "6px",
                  fontSize: "11px",
                  fontWeight: "500",
                  color: "#e65100",
                }}
              >
                5. Budget Approval Released
              </div>
              <i
                className="fas fa-arrow-right"
                style={{ color: "#999", fontSize: "12px" }}
              ></i>
            </div>
          </div>

          {/* External Stages */}
          <div style={{ marginBottom: "10px" }}>
            <div
              style={{
                display: "inline-block",
                padding: "4px 10px",
                backgroundColor: "#fff3e0",
                borderRadius: "4px",
                fontSize: "12px",
                fontWeight: "600",
                color: "#e65100",
                marginBottom: "10px",
              }}
            >
              <i
                className="fas fa-external-link-alt"
                style={{ marginRight: "6px" }}
              ></i>
              External Stages (MDA/Accountant General)
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "8px",
              }}
            >
              <div
                style={{
                  padding: "8px 14px",
                  backgroundColor: "#fff8e1",
                  border: "2px solid #ff9800",
                  borderRadius: "6px",
                  fontSize: "11px",
                  fontWeight: "500",
                  color: "#e65100",
                }}
              >
                Further Processing (MDA/AG)
              </div>
            </div>
          </div>

          {/* Note about Returns */}
          <div
            style={{
              marginTop: "15px",
              padding: "10px 12px",
              backgroundColor: "#fff3cd",
              borderLeft: "4px solid #ffc107",
              borderRadius: "4px",
            }}
          >
            <div style={{ display: "flex", alignItems: "start", gap: "8px" }}>
              <i
                className="fas fa-info-circle"
                style={{ color: "#856404", marginTop: "2px" }}
              ></i>
              <div>
                <strong style={{ fontSize: "12px", color: "#856404" }}>
                  Note:
                </strong>
                <p
                  style={{
                    fontSize: "11px",
                    color: "#856404",
                    margin: "4px 0 0 0",
                  }}
                >
                  If a status shows "Returned", the release is still within the
                  Budget Office and has been sent back to a previous stage for
                  corrections.
                </p>
              </div>
            </div>
          </div>
        </div>

        {releaseData && (
          <div
            style={{
              backgroundColor: "#fff",
              padding: "20px",
              borderRadius: "8px",
              marginBottom: "20px",
              border: "1px solid #e0e0e0",
            }}
          >
            <h6
              style={{
                marginBottom: "15px",
                color: "#333",
                borderBottom: "2px solid #51ce5bff",
                paddingBottom: "10px",
              }}
            >
              <i
                className="fas fa-info-circle"
                style={{ marginRight: "8px" }}
              ></i>
              Release Information
            </h6>
            <div className="row">
              <div className="col-md-4">
                <small style={{ color: "#666" }}>MDA Name:</small>
                <div style={{ fontWeight: "500", marginBottom: "10px" }}>
                  {releaseData.mda_name}
                </div>
              </div>
              <div className="col-md-4">
                <small style={{ color: "#666" }}>Amount:</small>
                <div
                  style={{
                    fontWeight: "600",
                    color: "#28a745",
                    marginBottom: "10px",
                  }}
                >
                  ₦ {formatNumber(releaseData.amount)}
                </div>
              </div>
              <div className="col-md-4">
                <small style={{ color: "#666" }}>Collection Date:</small>
                <div style={{ fontWeight: "500", marginBottom: "10px" }}>
                  {moment(releaseData.collection_date).format("MMM DD, YYYY")}
                </div>
              </div>
            </div>
          </div>
        )}

        {isLoading ? (
          <div
            className="text-center"
            style={{
              padding: "60px 20px",
              backgroundColor: "#fff",
              borderRadius: "8px",
            }}
          >
            <Spinner
              color="success"
              style={{ width: "3rem", height: "3rem" }}
            />
            <div className="mt-3" style={{ color: "#666", fontSize: "16px" }}>
              Loading approval history...
            </div>
          </div>
        ) : historyData.length === 0 ? (
          <div
            className="text-center"
            style={{
              padding: "60px 20px",
              backgroundColor: "#fff",
              borderRadius: "8px",
              color: "#999",
            }}
          >
            <i
              className="fas fa-inbox"
              style={{ fontSize: "64px", marginBottom: "20px", color: "#ddd" }}
            ></i>
            <div style={{ fontSize: "18px" }}>No history records found</div>
            <small>This release has no recorded history</small>
          </div>
        ) : (
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "8px",
              overflow: "hidden",
              border: "1px solid #e0e0e0",
            }}
          >
            <div
              style={{
                overflowX: "auto",
                maxHeight: "500px",
                overflowY: "auto",
              }}
            >
              <Table
                bordered
                hover
                style={{ marginBottom: 0, backgroundColor: "#fff" }}
              >
                <thead
                  style={{
                    backgroundColor: "#51ce5bff",
                    color: "#fff",
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                  }}
                >
                  <tr>
                    <th
                      style={{
                        padding: "12px",
                        fontSize: "13px",
                        width: "60px",
                      }}
                    >
                      Stage
                    </th>
                    <th style={{ padding: "12px", fontSize: "13px" }}>
                      Status
                    </th>
                    <th style={{ padding: "12px", fontSize: "13px" }}>
                      Officer
                    </th>
                    <th style={{ padding: "12px", fontSize: "13px" }}>Role</th>
                    <th style={{ padding: "12px", fontSize: "13px" }}>
                      Time Spent
                    </th>
                    <th style={{ padding: "12px", fontSize: "13px" }}>
                      Updated At
                    </th>
                    <th style={{ padding: "12px", fontSize: "13px" }}>
                      Remarks
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {historyData.map((item, index) => (
                    <tr key={item.id} style={{ verticalAlign: "middle" }}>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          fontWeight: "600",
                        }}
                      >
                        <Badge
                          color="secondary"
                          style={{ fontSize: "12px", padding: "6px 10px" }}
                        >
                          {item.stage_order}
                        </Badge>
                      </td>
                      <td style={{ padding: "12px" }}>
                        <Badge
                          color={getStatusColor(item.approval_status)}
                          style={{
                            fontSize: "11px",
                            padding: "6px 12px",
                            fontWeight: "500",
                          }}
                        >
                          {item.approval_status}
                        </Badge>
                      </td>
                      <td style={{ padding: "12px" }}>
                        <div style={{ fontWeight: "500", marginBottom: "2px" }}>
                          {item.officer.fullname}
                        </div>
                        {item.officer.email && (
                          <small style={{ color: "#666" }}>
                            {item.officer.email}
                          </small>
                        )}
                      </td>
                      <td style={{ padding: "12px" }}>
                        <span
                          style={{
                            fontSize: "12px",
                            padding: "4px 8px",
                            backgroundColor: "#e9ecef",
                            borderRadius: "4px",
                            color: "#495057",
                          }}
                        >
                          {item.officer.role}
                        </span>
                      </td>
                      <td style={{ padding: "12px" }}>
                        {item.time_spent.seconds ? (
                          <div>
                            <span
                              style={{
                                fontWeight: "600",
                                fontSize: "14px",
                                color: getTimeColor(item.time_spent.seconds),
                              }}
                            >
                              {item.time_spent.formatted}
                            </span>
                            <br />
                            <small style={{ color: "#999" }}>
                              {Math.round(item.time_spent.seconds / 3600)} hours
                            </small>
                          </div>
                        ) : (
                          <span style={{ color: "#999", fontSize: "12px" }}>
                            Initial Stage
                          </span>
                        )}
                      </td>
                      <td style={{ padding: "12px" }}>
                        <div style={{ fontSize: "13px" }}>
                          {moment(item.updated_at).format("MMM DD, YYYY")}
                        </div>
                        <small style={{ color: "#666" }}>
                          {moment(item.updated_at).format("hh:mm A")}
                        </small>
                      </td>
                      <td style={{ padding: "12px", maxWidth: "200px" }}>
                        {item.remarks ? (
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#495057",
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {item.remarks}
                          </div>
                        ) : (
                          <span style={{ color: "#999", fontSize: "12px" }}>
                            No remarks
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            <div
              style={{
                padding: "15px 20px",
                backgroundColor: "#f8f9fa",
                borderTop: "1px solid #e0e0e0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ fontSize: "14px", color: "#666" }}>
                <strong>Total Stages:</strong> {historyData.length}
              </div>
              {historyData.length > 1 && (
                <div style={{ fontSize: "14px", color: "#666" }}>
                  <strong>Total Processing Time:</strong>{" "}
                  {(() => {
                    const totalSeconds = historyData.reduce(
                      (sum, item) => sum + (item.time_spent.seconds || 0),
                      0,
                    );
                    const days = Math.floor(totalSeconds / (24 * 3600));
                    const hours = Math.floor(
                      (totalSeconds % (24 * 3600)) / 3600,
                    );
                    return `${days}d ${hours}h`;
                  })()}
                </div>
              )}
            </div>
          </div>
        )}
      </ModalBody>

      <ModalFooter style={{ backgroundColor: "#f8f9fa" }}>
        <Button
          color="secondary"
          onClick={toggle}
          style={{ fontWeight: "500", padding: "8px 24px" }}
        >
          <i className="fas fa-times" style={{ marginRight: "8px" }}></i>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ApprovalHistoryModal;
